import axios from 'axios';
import { toSameOriginImageProxyUrl } from './imageProxy';

const TO_RADIANS = Math.PI / 180;

/** Avoid CORS mode for blob/data/same-origin URLs (breaks crop export / tainted canvas). */
export function shouldUseAnonymousCrossOrigin(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    return false;
  }
  if (typeof window === 'undefined' || !window.location?.href) {
    return true;
  }
  try {
    const absolute = new URL(url, window.location.href);
    return absolute.origin !== window.location.origin;
  } catch {
    return false;
  }
}

export async function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    if (shouldUseAnonymousCrossOrigin(url)) {
      image.crossOrigin = 'anonymous';
    }
    image.src = url;
  });
}

/**
 * Load pixels for canvas export without tainting.
 * Remote signed image-proxy URLs are fetched via axios (relative path / auth) → blob URL.
 */
export async function loadImageForCropExport(imageSrc) {
  if (!imageSrc) {
    throw new Error('Missing image source');
  }
  if (imageSrc.startsWith('blob:') || imageSrc.startsWith('data:')) {
    return {
      image: await createImage(imageSrc),
      revoke: () => {},
    };
  }

  const fetchUrl = toSameOriginImageProxyUrl(imageSrc);
  const response = await axios.get(fetchUrl, {
    responseType: 'blob',
    skipConnectivityGuard: true,
    skipAuthRefresh: false,
  });
  const blob = response?.data;
  if (!(blob instanceof Blob)) {
    throw new Error('Image fetch did not return a blob');
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await createImage(objectUrl);
    return {
      image,
      revoke: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

export function rotateSize(width, height, rotation) {
  const rotRad = rotation * TO_RADIANS;
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/** PNG when round mask, transparent background, or PNG source; otherwise JPEG (legacy default). */
export function resolveCroppedOutputMimeType({ mimeType, cropShape, backgroundColor }) {
  if (cropShape === 'round' || backgroundColor === 'transparent') {
    return 'image/png';
  }
  if (mimeType === 'image/png') {
    return 'image/png';
  }
  return 'image/jpeg';
}

function applyRoundMask(ctx, width, height) {
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.fill();
}

function fillCropBackground(ctx, width, height, backgroundColor) {
  if (backgroundColor === 'transparent') {
    ctx.clearRect(0, 0, width, height);
    return;
  }
  ctx.fillStyle = backgroundColor ?? '#ffffff';
  ctx.fillRect(0, 0, width, height);
}

function drawCroppedAreaWithBackground(croppedCtx, sourceCanvas, pixelCrop, backgroundColor) {
  const outputWidth = pixelCrop.width;
  const outputHeight = pixelCrop.height;

  fillCropBackground(croppedCtx, outputWidth, outputHeight, backgroundColor);

  const destX = pixelCrop.x < 0 ? -pixelCrop.x : 0;
  const destY = pixelCrop.y < 0 ? -pixelCrop.y : 0;
  const sourceX = Math.max(0, pixelCrop.x);
  const sourceY = Math.max(0, pixelCrop.y);
  const sourceWidth = Math.min(outputWidth - destX, sourceCanvas.width - sourceX);
  const sourceHeight = Math.min(outputHeight - destY, sourceCanvas.height - sourceY);

  if (sourceWidth > 0 && sourceHeight > 0) {
    croppedCtx.drawImage(
      sourceCanvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      destX,
      destY,
      sourceWidth,
      sourceHeight,
    );
  }
}

/**
 * @param {string} imageSrc
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop
 * @param {{
 *   rotation?: number,
 *   cropShape?: 'rect'|'round',
 *   mimeType?: string,
 *   backgroundColor?: string|null,
 *   outputWidth?: number,
 *   outputHeight?: number,
 * }} [options]
 * @returns {Promise<Blob>}
 */
export default async function getCroppedImg(imageSrc, pixelCrop, options = {}) {
  if (!pixelCrop?.width || !pixelCrop?.height) {
    throw new Error('Missing crop area');
  }

  const rotation = options.rotation ?? 0;
  const cropShape = options.cropShape ?? 'rect';
  const backgroundColor = options.backgroundColor ?? '#ffffff';
  const outputMime = resolveCroppedOutputMimeType({
    mimeType: options.mimeType,
    cropShape,
    backgroundColor: options.backgroundColor === 'transparent' ? 'transparent' : null,
  });

  const { image, revoke } = await loadImageForCropExport(imageSrc);
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotation * TO_RADIANS);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');

    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;

    const resolvedBackground =
      options.backgroundColor === 'transparent' ? 'transparent' : backgroundColor;

    drawCroppedAreaWithBackground(croppedCtx, canvas, pixelCrop, resolvedBackground);

    if (cropShape === 'round') {
      applyRoundMask(croppedCtx, pixelCrop.width, pixelCrop.height);
    }

    const outputWidth = options.outputWidth;
    const outputHeight = options.outputHeight;
    let exportCanvas = croppedCanvas;
    if (
      outputWidth > 0 &&
      outputHeight > 0 &&
      (outputWidth !== croppedCanvas.width || outputHeight !== croppedCanvas.height)
    ) {
      exportCanvas = document.createElement('canvas');
      exportCanvas.width = outputWidth;
      exportCanvas.height = outputHeight;
      const exportCtx = exportCanvas.getContext('2d');
      if (resolvedBackground !== 'transparent') {
        exportCtx.fillStyle = resolvedBackground;
        exportCtx.fillRect(0, 0, outputWidth, outputHeight);
      }
      exportCtx.drawImage(croppedCanvas, 0, 0, outputWidth, outputHeight);
    }

    return await new Promise((resolve, reject) => {
      exportCanvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        outputMime,
        outputMime === 'image/jpeg' ? 0.92 : undefined,
      );
    });
  } finally {
    revoke();
  }
}
